import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["messages", "input", "loading", "submitButton", "header", "body", "chevron", "mentionDropdown"]
  static values = { projectId: Number, csrfToken: String }

  connect() {
    this.isExpanded = false
    this.mentionStartIndex = -1
  }

  // Handle @ mention input
  handleInput(event) {
    const value = this.inputTarget.value
    const cursorPos = this.inputTarget.selectionStart
    
    // Find if we're in an @ mention context
    const textBeforeCursor = value.substring(0, cursorPos)
    const atIndex = textBeforeCursor.lastIndexOf('@')
    
    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(atIndex + 1)
      // Show dropdown if @ is at start or after a space, and no space after @
      const charBeforeAt = atIndex === 0 ? ' ' : value.charAt(atIndex - 1)
      if ((charBeforeAt === ' ' || charBeforeAt === '') && !textAfterAt.includes(' ')) {
        this.mentionStartIndex = atIndex
        this.showMentionDropdown(textAfterAt)
        return
      }
    }
    
    this.hideMentionDropdown()
  }

  handleKeydown(event) {
    if (!this.hasMentionDropdownTarget) return
    
    if (this.mentionDropdownTarget.classList.contains('hidden')) return
    
    // Handle escape to close dropdown
    if (event.key === 'Escape') {
      this.hideMentionDropdown()
      event.preventDefault()
    }
  }

  showMentionDropdown(filter = '') {
    if (!this.hasMentionDropdownTarget) return
    
    const dropdown = this.mentionDropdownTarget
    const buttons = dropdown.querySelectorAll('button[data-mention-name]')
    
    // Filter options based on typed text
    let visibleCount = 0
    buttons.forEach(btn => {
      const name = btn.dataset.mentionName.toLowerCase()
      if (name.includes(filter.toLowerCase())) {
        btn.classList.remove('hidden')
        visibleCount++
      } else {
        btn.classList.add('hidden')
      }
    })
    
    // Show dropdown if there are matches
    if (visibleCount > 0) {
      dropdown.classList.remove('hidden')
    } else {
      dropdown.classList.add('hidden')
    }
  }

  hideMentionDropdown() {
    if (!this.hasMentionDropdownTarget) return
    this.mentionDropdownTarget.classList.add('hidden')
    this.mentionStartIndex = -1
  }

  selectMention(event) {
    event.preventDefault()
    const name = event.currentTarget.dataset.mentionName
    
    if (this.mentionStartIndex !== -1) {
      const value = this.inputTarget.value
      const beforeMention = value.substring(0, this.mentionStartIndex)
      const afterCursor = value.substring(this.inputTarget.selectionStart)
      
      // Insert the name
      this.inputTarget.value = `${beforeMention}@${name} ${afterCursor}`
      
      // Move cursor to after the inserted name
      const newCursorPos = this.mentionStartIndex + name.length + 2 // +2 for @ and space
      this.inputTarget.setSelectionRange(newCursorPos, newCursorPos)
    }
    
    this.hideMentionDropdown()
    this.inputTarget.focus()
  }

  toggle() {
    this.isExpanded = !this.isExpanded
    
    if (this.isExpanded) {
      this.bodyTarget.classList.remove("hidden")
      this.chevronTarget.classList.add("rotate-180")
      // Focus input after animation
      setTimeout(() => {
        this.inputTarget.focus()
        this.scrollToBottom()
      }, 100)
    } else {
      this.bodyTarget.classList.add("hidden")
      this.chevronTarget.classList.remove("rotate-180")
    }
  }

  expand() {
    if (!this.isExpanded) {
      this.toggle()
    }
  }

  async sendMessage(event) {
    event.preventDefault()
    
    const question = this.inputTarget.value.trim()
    if (!question) return
    
    // Clear input and disable submit
    this.inputTarget.value = ""
    this.submitButtonTarget.disabled = true
    
    // Add user message to UI immediately
    this.appendMessage("user", question)
    
    // Show loading indicator
    this.loadingTarget.classList.remove("hidden")
    this.scrollToBottom()
    
    try {
      const response = await fetch(`/projects/${this.projectIdValue}/chats`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify({ question })
      })
      
      const data = await response.json()
      
      if (data.error) {
        this.appendMessage("assistant", `Error: ${data.error}`)
      } else if (data.status === "pending") {
        // Async mode - poll for the response
        await this.pollForResponse(data.id)
      } else {
        this.appendMessage("assistant", data.answer, data.tokens_used?.total)
      }
      
    } catch (error) {
      console.error("Chat error:", error)
      this.appendMessage("assistant", "Sorry, I encountered an error. Please try again.")
    } finally {
      this.loadingTarget.classList.add("hidden")
      this.submitButtonTarget.disabled = false
      this.inputTarget.focus()
      this.scrollToBottom()
    }
  }

  async pollForResponse(messageId) {
    const maxAttempts = 60 // Poll for up to 2 minutes (60 * 2 seconds)
    let attempts = 0
    
    while (attempts < maxAttempts) {
      await this.sleep(2000) // Wait 2 seconds between polls
      
      try {
        const response = await fetch(`/projects/${this.projectIdValue}/chats/${messageId}`, {
          headers: {
            "Accept": "application/json"
          }
        })
        
        const data = await response.json()
        
        if (data.status === "completed") {
          this.appendMessage("assistant", data.content)
          return
        } else if (data.status === "error") {
          this.appendMessage("assistant", data.content || "An error occurred while processing your request.")
          return
        }
        // Still pending, continue polling
        
      } catch (error) {
        console.error("Poll error:", error)
      }
      
      attempts++
    }
    
    // Timeout
    this.appendMessage("assistant", "The request is taking longer than expected. Please try again.")
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  askQuickQuestion(event) {
    event.stopPropagation() // Prevent toggle from firing
    const question = event.target.dataset.question
    if (question) {
      this.expand() // Make sure panel is open
      this.inputTarget.value = question
      this.sendMessage(event)
    }
  }

  async clearHistory() {
    if (!confirm("Clear chat history for this project?")) return
    
    // Clear the messages display
    this.messagesTarget.innerHTML = this.getWelcomeHTML()
    this.scrollToBottom()
  }

  appendMessage(role, content, tokensUsed = null) {
    // Remove welcome message if present
    const welcome = this.messagesTarget.querySelector(".text-center.py-8")
    if (welcome) welcome.remove()
    
    const time = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    const tokensHtml = tokensUsed ? ` · ${tokensUsed} tokens` : ''
    
    const messageHtml = role === "user" 
      ? this.getUserMessageHTML(content, time)
      : this.getAssistantMessageHTML(content, time, tokensHtml)
    
    this.messagesTarget.insertAdjacentHTML("beforeend", messageHtml)
    this.scrollToBottom()
  }

  getUserMessageHTML(content, time) {
    // Highlight @mentions in the content
    const highlightedContent = this.escapeHtml(content).replace(
      /@([A-Za-z]+ [A-Za-z]+)/g, 
      '<span class="font-semibold text-blue-200">@$1</span>'
    )
    
    return `
      <div class="flex items-start gap-3 flex-row-reverse">
        <div class="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
          <svg class="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <div class="flex-1 max-w-[85%] text-right">
          <div class="inline-block rounded-lg px-4 py-2.5 text-sm bg-blue-600 text-white shadow-sm">
            ${highlightedContent}
          </div>
          <div class="mt-1 text-xs text-slate-400">${time}</div>
        </div>
      </div>
    `
  }

  getAssistantMessageHTML(content, time, tokensHtml) {
    return `
      <div class="flex items-start gap-3">
        <div class="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
          </svg>
        </div>
        <div class="flex-1 max-w-[85%]">
          <div class="inline-block rounded-lg px-4 py-3 text-sm bg-white text-slate-900 shadow-sm">
            <div class="prose prose-sm prose-slate max-w-none chat-markdown">
              ${this.formatMarkdown(content)}
            </div>
          </div>
          <div class="mt-1 text-xs text-slate-400">${time}${tokensHtml}</div>
        </div>
      </div>
    `
  }

  getWelcomeHTML() {
    return `
      <div class="text-center py-6">
        <div class="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
          <svg class="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
          </svg>
        </div>
        <h4 class="text-base font-medium text-slate-900 mb-1">What would you like to know?</h4>
        <p class="text-sm text-slate-500 max-w-md mx-auto mb-5">
          I can answer questions about this project using data from meetings, people, todos, and files.
        </p>
        <div class="flex flex-wrap justify-center gap-2">
          <button type="button" class="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors" data-action="click->project-chat#askQuickQuestion" data-question="What's the current status of this project?">📊 Current status</button>
          <button type="button" class="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors" data-action="click->project-chat#askQuickQuestion" data-question="Who is working on this project and what are their roles?">👥 Team members</button>
          <button type="button" class="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors" data-action="click->project-chat#askQuickQuestion" data-question="What are the outstanding risks or concerns?">⚠️ Risks</button>
          <button type="button" class="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors" data-action="click->project-chat#askQuickQuestion" data-question="What todos are overdue or due soon?">✅ Pending todos</button>
          <button type="button" class="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors" data-action="click->project-chat#askQuickQuestion" data-question="What were the key decisions from recent meetings?">📝 Recent decisions</button>
        </div>
      </div>
    `
  }

  formatMarkdown(text) {
    if (!text) return ''
    
    // Escape HTML first
    let html = this.escapeHtml(text)
    
    // Convert headers (### before ## before #)
    html = html.replace(/^### (.+)$/gm, '<h4 class="text-sm font-bold text-slate-800 mt-3 mb-1">$1</h4>')
    html = html.replace(/^## (.+)$/gm, '<h3 class="text-base font-bold text-slate-900 mt-4 mb-2 pb-1 border-b border-slate-200">$1</h3>')
    html = html.replace(/^# (.+)$/gm, '<h2 class="text-lg font-bold text-slate-900 mt-4 mb-2">$1</h2>')
    
    // Convert markdown bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
    
    // Convert markdown italic
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
    
    // Convert inline code
    html = html.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-mono">$1</code>')
    
    // Highlight @mentions
    html = html.replace(/@([A-Za-z]+ [A-Za-z]+)/g, '<span class="font-semibold text-blue-600">@$1</span>')
    
    // Convert numbered lists
    html = html.replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4">$2</li>')
    
    // Convert bullet points (handle both - and •)
    html = html.replace(/^[-•] (.+)$/gm, '<li class="ml-4">$1</li>')
    
    // Wrap consecutive list items in ul/ol
    html = html.replace(/(<li[^>]*>.+<\/li>(\s|<br>)*)+/g, (match) => {
      return `<ul class="list-disc list-inside my-2 space-y-1">${match.replace(/<br>/g, '')}</ul>`
    })
    
    // Convert horizontal rules
    html = html.replace(/^---$/gm, '<hr class="my-3 border-slate-200">')
    
    // Convert line breaks (but not after block elements)
    html = html.replace(/\n(?!<\/?(h[1-4]|ul|ol|li|hr|div))/g, '<br>')
    
    // Clean up extra line breaks
    html = html.replace(/(<br>){3,}/g, '<br><br>')
    html = html.replace(/<br>(<\/?(h[1-4]|ul|ol|hr))/g, '$1')
    
    return html
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }
}

